<template>
    <v-dialog v-model="show" hide-overlay fullscreen transition="dialog-bottom-transition">
        <div>
            <v-card v-model="show">
                <v-toolbar dark color="blue">
                    <v-toolbar-title class="my-0 py-0">Indicador</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <div>
                        <v-btn icon @click="show = false">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </div>
                </v-toolbar>
            </v-card>

            <!-- Contenido cargado -->
            <v-card v-if="detalle" class="pa-4" outlined v-model="show">
                <!-- Cabecera -->
                <v-card-title class="headline">
                    {{ detalle.indicador.codigo }} - {{ detalle.indicador.nombre }}
                </v-card-title>

                <v-row class="mb-4">
                    <v-col cols="12" md="4">
                        <strong>Instructivo:</strong>
                        <div class="body-2">
                            {{ detalle.indicador.instructivo }}
                        </div>
                    </v-col>
                    <v-col cols="12" md="4">
                        <strong>Objetivo:</strong>
                        <div class="body-2">
                            {{ detalle.indicador.objetivo }}
                        </div>
                    </v-col>
                    <v-col cols="12" md="4">
                        <strong>Unidad:</strong>
                        <div class="body-2">
                            {{ detalle.indicador.unidad }}
                        </div>
                    </v-col>
                </v-row>

                <!-- Tabla -->
                <v-card class="mb-4" outlined>
                    <v-card-title class="subtitle-1">
                        Detalle por año
                    </v-card-title>

                    <v-data-table :headers="tablaHeaders" :items="tablaItems" dense :items-per-page="50"
                        class="elevation-0">
                        <!-- Formateo del campo valorIndicador -->
                        <template v-slot:item.valorIndicador="{ item }">
                            {{ formatValorIndicador(item.valorIndicador) }}
                        </template>
                    </v-data-table>
                </v-card>

                <!-- Gráfico SVG propio -->
                <v-card outlined>
                    <v-card-title class="subtitle-1">
                        Evolución del indicador
                    </v-card-title>

                    <v-card-text>
                        <!-- Resumen arriba del gráfico -->
                        <div class="d-flex justify-space-between align-center mb-3" v-if="ultimoAnioLabel">
                            <div>
                                <div class="caption grey--text text--darken-1">
                                    Último año
                                </div>
                                <div class="title font-weight-bold">
                                    {{ ultimoAnioLabel }}
                                </div>
                                <div class="subtitle-2" v-if="ultimoValorFormat">
                                    {{ ultimoValorFormat }}
                                </div>
                            </div>

                            <div v-if="variacionTexto" class="text-right">
                                <div class="caption grey--text text--darken-1">
                                    vs año anterior
                                </div>
                                <div :class="['subtitle-2', variacionColorClass]">
                                    {{ variacionTexto }}
                                </div>
                            </div>
                        </div>

                        <div v-if="hasAnyData" class="chart-wrapper">
                            <svg viewBox="0 0 100 60" preserveAspectRatio="none" class="chart-svg">
                                <!-- Fondo -->
                                <rect x="0" y="0" width="100" height="60" class="chart-bg" />

                                <!-- Línea de cero (si 0 está dentro del rango) -->
                                <line v-if="zeroLineY !== null" x1="0" :y1="zeroLineY" x2="100" :y2="zeroLineY"
                                    class="chart-zero-line" />

                                <!-- Línea de valores -->
                                <polyline v-if="polylineValues" :points="polylineValues" class="chart-line-values" />

                                <!-- Línea de referencia -->
                                <polyline v-if="polylineRefs" :points="polylineRefs" class="chart-line-ref" />

                                <!-- Puntos últimos (valor y referencia) -->
                                <circle v-if="lastValuePoint" :cx="lastValuePoint.x" :cy="lastValuePoint.y" r="1.2"
                                    class="chart-point-values" />
                                <circle v-if="lastRefPoint" :cx="lastRefPoint.x" :cy="lastRefPoint.y" r="1.2"
                                    class="chart-point-ref" />
                            </svg>

                            <!-- Eje X simple con años -->
                            <div class="chart-x-labels">
                                <div v-for="(label, index) in chartLabels" :key="index" class="chart-x-label">
                                    {{ label }}
                                </div>
                            </div>
                        </div>

                        <div v-else class="caption grey--text text--darken-1">
                            No hay datos suficientes para graficar.
                        </div>
                    </v-card-text>
                </v-card>
            </v-card>

            <!-- Loading -->
            <v-skeleton-loader v-else-if="loading" type="card, table" class="mt-4" />

            <!-- Error -->
            <v-alert v-else-if="error" type="error" class="ma-4" dense outlined>
                {{ error }}
            </v-alert>
        </div>
    </v-dialog>
</template>

<script>
import axios from "../../axios-auth";

export default {
    name: 'IndicadorDetalle',

    props: ["visible", "indicador"],

    data() {
        return {
            detalle: null,
            loading: false,
            error: null,
            indicadorId: "",
        }
    },

    computed: {
        show: {
            get() {
                if (this.visible === true) {
                    this.cargarindicador();
                }
                return this.visible;
            },
            set(value) {
                if (!value) {
                    this.limpiar();
                    this.$emit("close");
                }
            },
        },

        tablaHeaders() {
            if (!this.detalle || !this.detalle.columnas) return []
            return this.detalle.columnas.map(c => ({
                text: c.label,
                value: c.key
            }))
        },

        tablaItems() {
            return (this.detalle && this.detalle.filas) ? this.detalle.filas : []
        },

        // =========================
        //   DATOS CRUDOS PARA EL GRÁFICO
        // =========================

        // Valores del indicador (Y)
        rawChartValues() {
            if (!this.detalle || !this.detalle.filas) return []
            const yKey = (this.detalle.chart && this.detalle.chart.yKey) || 'valorIndicador'
            return this.detalle.filas.map(f => {
                const v = f[yKey]
                if (v == null || v === '') return null
                return Number(v)
            })
        },

        // Referencias año a año (detalle.filas[i].referencia)
        rawRefValues() {
            if (!this.detalle || !this.detalle.filas) return []
            return this.detalle.filas.map(f => {
                if (f.referencia == null || f.referencia === '') return null

                let raw = f.referencia
                if (typeof raw === 'string') {
                    raw = raw.replace(',', '.')
                }

                const n = Number(raw)
                return isNaN(n) ? null : n
            })
        },

        chartLabels() {
            if (!this.detalle || !this.detalle.filas) return []
            const xKey = (this.detalle.chart && this.detalle.chart.xKey) || 'anio'
            return this.detalle.filas.map(f => f[xKey])
        },

        hasAnyData() {
            const all = [...this.rawChartValues, ...this.rawRefValues]
            return all.some(v => v != null)
        },

        // =========================
        //   ESCALA GLOBAL (min / max)
        // =========================

        globalMin() {
            const valores = [...this.rawChartValues, ...this.rawRefValues]
                .filter(v => v != null)

            if (!valores.length) return null

            let min = Math.min(...valores)
            let max = Math.max(...valores)

            // Si todos iguales, expandimos un poco para que no se vea línea chata
            if (min === max) {
                min -= 1
                max += 1
            }

            return min
        },

        globalMax() {
            const valores = [...this.rawChartValues, ...this.rawRefValues]
                .filter(v => v != null)

            if (!valores.length) return null

            let min = Math.min(...valores)
            let max = Math.max(...valores)

            if (min === max) {
                min -= 1
                max += 1
            }

            return max
        },

        // Posición Y de la línea de cero dentro del SVG (0–60) o null si no está en el rango
        zeroLineY() {
            if (this.globalMin == null || this.globalMax == null) return null
            const min = this.globalMin
            const max = this.globalMax
            if (0 < min || 0 > max) return null // 0 fuera del rango

            const rango = max - min
            const t = (0 - min) / rango // 0..1
            // y va de 10 a 50 (dejamos margen arriba/abajo)
            const y = 50 - t * 40
            return y
        },

        // =========================
        //   POLYLINES
        // =========================

        polylineValues() {
            return this.buildPolyline(this.rawChartValues)
        },

        polylineRefs() {
            return this.buildPolyline(this.rawRefValues)
        },

        // =========================
        //   PUNTOS ÚLTIMOS
        // =========================

        lastValuePoint() {
            return this.getLastPoint(this.rawChartValues)
        },

        lastRefPoint() {
            return this.getLastPoint(this.rawRefValues)
        },

        // =========================
        //   RESUMEN (último valor, variación)
        // =========================

        chartValuesForSummary() {
            return this.rawChartValues.filter(v => v != null)
        },

        ultimoIndex() {
            return this.chartValuesForSummary.length
                ? this.chartValuesForSummary.length - 1
                : null
        },

        ultimoAnioLabel() {
            if (this.ultimoIndex === null) return ''
            const filasValidas = this.detalle.filas.filter((_, idx) => this.rawChartValues[idx] != null)
            if (!filasValidas.length) return ''
            const xKey = (this.detalle.chart && this.detalle.chart.xKey) || 'anio'
            return filasValidas[this.ultimoIndex][xKey]
        },

        ultimoValor() {
            if (this.ultimoIndex === null) return null
            return this.chartValuesForSummary[this.ultimoIndex]
        },

        ultimoValorFormat() {
            if (this.ultimoValor == null) return ''
            const unidad = this.detalle && this.detalle.indicador && this.detalle.indicador.unidad

            if (unidad === '%') {
                return Number(this.ultimoValor).toFixed(2) + ' %'
            }

            return Number(this.ultimoValor).toFixed(2)
        },

        variacionTexto() {
            if (this.chartValuesForSummary.length < 2) return ''
            const last = this.chartValuesForSummary[this.chartValuesForSummary.length - 1]
            const prev = this.chartValuesForSummary[this.chartValuesForSummary.length - 2]
            if (prev == null || prev === 0) return ''
            const delta = ((last - prev) / prev) * 100
            const signo = delta > 0 ? '+' : ''
            return `${signo}${delta.toFixed(1)} %`
        },

        variacionColorClass() {
            if (!this.variacionTexto) return ''
            const last = this.chartValuesForSummary[this.chartValuesForSummary.length - 1]
            const prev = this.chartValuesForSummary[this.chartValuesForSummary.length - 2]
            return last >= prev
                ? 'green--text text--darken-2'
                : 'red--text text--darken-2'
        }
    },

    methods: {
        limpiar() {
            this.detalle = null;
            this.loading = false;
            this.error = null;
            this.indicadorId = "";
        },

        // Construye la string "x,y x,y ..." para polyline a partir de una serie de valores
        buildPolyline(values) {
            if (!values || !values.length) return ''

            if (this.globalMin == null || this.globalMax == null) return ''

            const min = this.globalMin
            const max = this.globalMax
            const rango = max - min || 1

            const n = values.length
            if (n === 0) return ''

            const points = []

            values.forEach((v, idx) => {
                if (v == null) return // salteamos nulls (no punto)

                const tX = n === 1 ? 0.5 : idx / (n - 1) // 0..1
                const x = 5 + tX * 90 // margen 5% a izquierda/derecha

                const tY = (v - min) / rango // 0..1
                const y = 50 - tY * 40 // margen 10 arriba (10) y 10 abajo (50)

                points.push(`${x},${y}`)
            })

            return points.join(' ')
        },

        // Devuelve el último punto (x,y) de una serie, para dibujar un círculo
        getLastPoint(values) {
            if (!values || !values.length) return null
            if (this.globalMin == null || this.globalMax == null) return null

            const min = this.globalMin
            const max = this.globalMax
            const rango = max - min || 1

            const n = values.length
            let idx = -1
            for (let i = n - 1; i >= 0; i--) {
                if (values[i] != null) {
                    idx = i
                    break
                }
            }
            if (idx === -1) return null

            const v = values[idx]
            const tX = n === 1 ? 0.5 : idx / (n - 1)
            const x = 5 + tX * 90
            const tY = (v - min) / rango
            const y = 50 - tY * 40

            return { x, y }
        },

        cargarindicador() {
            let self = this;

            // Mapeo de códigos a endpoints (tu lógica, sin tocar)
            if (this.indicador == "E3A") this.indicadorId = "GetDetalleE3A"
            if (this.indicador == "C1") this.indicadorId = "GetDetalleC1"
            if (this.indicador == "C2A") this.indicadorId = "GetDetalleC2A"
            if (this.indicador == "C2B") this.indicadorId = "GetDetalleC2B"
            if (this.indicador == "C2C") this.indicadorId = "GetDetalleC2C"
            if (this.indicador == "C3F") this.indicadorId = "GetDetalleC3F"
            if (this.indicador == "C3O") this.indicadorId = "GetDetalleC3O"
            if (this.indicador == "C3P") this.indicadorId = "GetDetalleC3P"
            if (this.indicador == "C5") this.indicadorId = "GetDetalleC5"
            if (this.indicador == "C6") this.indicadorId = "GetDetalleC6"
            if (this.indicador == "C7") this.indicadorId = "GetDetalleC7"
            if (this.indicador == "E10") this.indicadorId = "GetDetalleE10"
            if (this.indicador == "E11") this.indicadorId = "GetDetalleE11"
            if (this.indicador == "E12") this.indicadorId = "GetDetalleE12"
            if (this.indicador == "E13") this.indicadorId = "GetDetalleE13"
            if (this.indicador == "E14") this.indicadorId = "GetDetalleE14"
            if (this.indicador == "E16") this.indicadorId = "GetDetalleE16"
            if (this.indicador == "E18T") this.indicadorId = "GetDetalleE18T"
            if (this.indicador == "E19T") this.indicadorId = "GetDetalleE19T"
            if (this.indicador == "E20") this.indicadorId = "GetDetalleE20"
            if (this.indicador == "E21") this.indicadorId = "GetDetalleE21"
            if (this.indicador == "E3C") this.indicadorId = "GetDetalleE3C"
            if (this.indicador == "E9") this.indicadorId = "GetDetalleE9"
            if (this.indicador == "G10") this.indicadorId = "GetDetalleG10"
            if (this.indicador == "G12") this.indicadorId = "GetDetalleG12"
            if (this.indicador == "G12AC") this.indicadorId = "GetDetalleG12AC"
            if (this.indicador == "G12BA") this.indicadorId = "GetDetalleG12BA"
            if (this.indicador == "G12F") this.indicadorId = "GetDetalleG12F"
            if (this.indicador == "G14") this.indicadorId = "GetDetalleG14"
            if (this.indicador == "G16") this.indicadorId = "GetDetalleG16"
            if (this.indicador == "G17") this.indicadorId = "GetDetalleG17"
            if (this.indicador == "G18") this.indicadorId = "GetDetalleG18"
            if (this.indicador == "G19") this.indicadorId = "GetDetalleG19"
            if (this.indicador == "G2") this.indicadorId = "GetDetalleG2"
            if (this.indicador == "G5") this.indicadorId = "GetDetalleG5"
            if (this.indicador == "G5O") this.indicadorId = "GetDetalleG5O"
            if (this.indicador == "G5P") this.indicadorId = "GetDetalleG5P"
            if (this.indicador == "G6") this.indicadorId = "GetDetalleG6"
            if (this.indicador == "G6O") this.indicadorId = "GetDetalleG6O"
            if (this.indicador == "G6P") this.indicadorId = "GetDetalleG6P"
            if (this.indicador == "G6+10") this.indicadorId = "GetDetalleG610"
            if (this.indicador == "G7A") this.indicadorId = "GetDetalleG7A"
            if (this.indicador == "G7AO") this.indicadorId = "GetDetalleG7AO"
            if (this.indicador == "G7AP") this.indicadorId = "GetDetalleG7AP"
            if (this.indicador == "G7B") this.indicadorId = "GetDetalleG7B"
            if (this.indicador == "G7BO") this.indicadorId = "GetDetalleG7BO"
            if (this.indicador == "G7BP") this.indicadorId = "GetDetalleG7BP"
            if (this.indicador == "G8") this.indicadorId = "GetDetalleG8"
            if (this.indicador == "G8O") this.indicadorId = "GetDetalleG8O"
            if (this.indicador == "G8P") this.indicadorId = "GetDetalleG8P"
            if (this.indicador == "R1") this.indicadorId = "GetDetalleR1"
            if (this.indicador == "R2") this.indicadorId = "GetDetalleR2"
            if (this.indicador == "R2T") this.indicadorId = "GetDetalleR2T"
            if (this.indicador == "R3") this.indicadorId = "GetDetalleR3"
            if (this.indicador == "R3T") this.indicadorId = "GetDetalleR3T"
            if (this.indicador == "R3A") this.indicadorId = "GetDetalleR3A"
            if (this.indicador == "R3B") this.indicadorId = "GetDetalleR3B"
            if (this.indicador == "R3AT") this.indicadorId = "GetDetalleR3AT"
            if (this.indicador == "R3BT") this.indicadorId = "GetDetalleR3BT"
            if (this.indicador == "R4") this.indicadorId = "GetDetalleR4"
            if (this.indicador == "R4T") this.indicadorId = "GetDetalleR4T"

            axios
                .get(`/Indicadores/${this.indicadorId}?`)
                .then((response) => {
                    self.detalle = response.data;
                })
                .catch((error) => {
                    console.log(error);
                });
        },

        formatValorIndicador(valor) {
            if (valor == null) return ''
            const unidad = this.detalle && this.detalle.indicador && this.detalle.indicador.unidad

            if (unidad === '%') {
                return Number(valor).toFixed(2) + ' %'
            }

            return Number(valor).toFixed(2)
        }
    }
}
</script>

<style scoped>
.chart-wrapper {
    width: 100%;
    height: 220px;
}

.chart-svg {
    width: 100%;
    height: 100%;
    display: block;
}

.chart-bg {
    fill: #fafafa;
}

.chart-zero-line {
    stroke: #9e9e9e;
    stroke-width: 0.2;
    stroke-dasharray: 1, 2;
}

/* Línea valores */
.chart-line-values {
    fill: none;
    stroke: #388E3C;
    stroke-width: 0.6;
}

/* Línea referencia */
.chart-line-ref {
    fill: none;
    stroke: #F44336;
    stroke-width: 0.6;
}

/* Puntos */
.chart-point-values {
    fill: #388E3C;
}

.chart-point-ref {
    fill: #F44336;
}

/* Labels eje X */
.chart-x-labels {
    display: flex;
    justify-content: space-between;
    margin-top: 4px;
}

.chart-x-label {
    font-size: 11px;
    color: rgba(0, 0, 0, 0.54);
    text-align: center;
    flex: 1 1 auto;
}
</style>
